/**
 * VedangJyotisham V6.2.0 — Muhurta Phase 17 Lunar/Solar Month, Ritu & Ayana Engine
 * Implements authoritative calendar facts and rule evaluations for:
 * - Lunar Month (Amanta default, Purnimanta optional override)
 * - Solar Month (derived from Phase 16 Sankranti facts)
 * - Adhika Masa (Intercalary month detection & rule evaluation)
 * - Kshaya Masa (Depleted month detection & rule evaluation)
 * - Ritu (Seasons) & Ayana (Uttarayana / Dakshinayana)
 */

import { detectSankrantiAroundDate } from './muhurtaPhase16SolarLunarEngine';
import * as _Astronomy from 'astronomy-engine';
const Astronomy: any = (_Astronomy as any).Observer ? _Astronomy : ((_Astronomy as any).default || _Astronomy);
import { calculateAuthoritativePanchanga, VEDIC_MASAS, RITUS } from './panchangaEngine';
import { getLahiriAyanamsha } from './vedicEngine';

function siderealSunSign(date: Date): number {
  const time = Astronomy.MakeTime(date);
  const longitude = (Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Sun, time, true)).elon - getLahiriAyanamsha(time) + 360) % 360;
  return Math.floor(longitude / 30);
}

export interface CalendarMonthContext {
  calendarSystem: 'AMANTA' | 'PURNIMANTA';
  lunarMonthName: string;
  solarMonthName: string;
  isAdhikaMasa: boolean;
  isKshayaMasa: boolean;
  rituName: string;
  ayana: 'Uttarayan' | 'Dakshinayan';
}

export interface Phase17EvaluationResult {
  factorId: string;
  factorName: string;
  category: 'LUNAR_MONTH' | 'SOLAR_MONTH' | 'ADHIKA_MASA' | 'KSHAYA_MASA' | 'RITU' | 'AYANA';
  classification: 'SUPPORT' | 'WARNING' | 'HARD_EXCLUSION' | 'INFORMATIONAL' | 'NEUTRAL';
  scoreImpact: number;
  description: string;
  evidence: CalendarMonthContext;
}

/**
 * Calculates calendar month, Ritu, and Ayana context for a given date and location.
 */
export function calculateCalendarContext(
  testDate: Date,
  latitude: number,
  longitude: number,
  calendarSystem: 'AMANTA' | 'PURNIMANTA' = 'AMANTA'
): CalendarMonthContext {
  const panchang = calculateAuthoritativePanchanga(testDate, { latitude, longitude });
  const sunLon = panchang.ephemeris.sunLongitude;
  const sunSignIdx = Math.floor(sunLon / 30);

  // Solar Month derived from Sankranti / Sun sign
  const solarMonthName = VEDIC_MASAS[(sunSignIdx + 1) % 12];
  
  // A lunation is bounded by consecutive astronomical new moons. The sidereal
  // solar sign at those boundaries identifies skipped/repeated-sign months.
  const time = Astronomy.MakeTime(testDate);
  const previousNewMoon = Astronomy.SearchMoonPhase(0, time, -35);
  const nextNewMoon = Astronomy.SearchMoonPhase(0, time, 35);
  const previousSign = previousNewMoon ? siderealSunSign(previousNewMoon.date) : sunSignIdx;
  const nextSign = nextNewMoon ? siderealSunSign(nextNewMoon.date) : sunSignIdx;
  const signAdvance = (nextSign - previousSign + 12) % 12;
  const isAdhikaMasa = Boolean(previousNewMoon && nextNewMoon && signAdvance === 0);
  const isKshayaMasa = Boolean(previousNewMoon && nextNewMoon && signAdvance > 1);
  let lunarMonthIndex = (previousSign + 1) % 12;
  if (calendarSystem === 'PURNIMANTA') {
    const previousFullMoon = Astronomy.SearchMoonPhase(180, time, -35);
    if (previousFullMoon && previousNewMoon && previousFullMoon.date > previousNewMoon.date) {
      lunarMonthIndex = (lunarMonthIndex + 1) % 12;
    }
  }
  const lunarMonthName = VEDIC_MASAS[lunarMonthIndex];

  // Ritu
  const ritu = RITUS.find(r => r.months.includes(lunarMonthName)) || RITUS[0];
  const rituName = ritu.nameEn;

  // Ayana
  const tropicalSunLongitude = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Sun, time, true)).elon;
  const ayana: 'Uttarayan' | 'Dakshinayan' = (tropicalSunLongitude >= 270 || tropicalSunLongitude < 90) ? 'Uttarayan' : 'Dakshinayan';

  return {
    calendarSystem,
    lunarMonthName,
    solarMonthName,
    isAdhikaMasa,
    isKshayaMasa,
    rituName,
    ayana
  };
}

/**
 * Evaluates Phase 17 Lunar/Solar Month, Ritu & Ayana rules for an activity.
 */
export function evaluatePhase17CalendarRules(
  testDate: Date,
  latitude: number,
  longitude: number,
  activityName: string,
  calendarSystem: 'AMANTA' | 'PURNIMANTA' = 'AMANTA'
): Phase17EvaluationResult {
  const context = calculateCalendarContext(testDate, latitude, longitude, calendarSystem);
  const isSpiritual = activityName.toLowerCase().includes('spiritual') || activityName.toLowerCase().includes('sadhana') || activityName.toLowerCase().includes('charity');

  let classification: 'SUPPORT' | 'WARNING' | 'HARD_EXCLUSION' | 'INFORMATIONAL' | 'NEUTRAL' = 'INFORMATIONAL';
  let scoreImpact = 0;
  let description = `Solar Month: ${context.solarMonthName}, Ritu: ${context.rituName}, Ayana: ${context.ayana} (${context.calendarSystem} system).`;
  let category: Phase17EvaluationResult['category'] = 'RITU';

  if (context.isAdhikaMasa) {
    category = 'ADHIKA_MASA';
    if (isSpiritual) {
      classification = 'SUPPORT';
      scoreImpact = 20;
      description = `Adhika Masa (Purushottam Masa) active. Highly auspicious for spiritual sadhana, japa, and charity.`;
    } else {
      classification = 'HARD_EXCLUSION';
      scoreImpact = -45;
      description = `⛔ Adhika Masa (Intercalary Month): Restricted for material samskaras (marriage, griha pravesh, thread ceremony).`;
    }
  } else if (context.isKshayaMasa) {
    category = 'KSHAYA_MASA';
    classification = 'HARD_EXCLUSION';
    scoreImpact = -50;
    description = `⛔ Kshaya Masa (Depleted Month): Restricted for major auspicious beginnings.`;
  } else if (context.ayana === 'Uttarayan' && /commerce|business|worldly|secular/.test(activityName.toLowerCase())) {
    category = 'AYANA';
    classification = 'SUPPORT';
    scoreImpact = 15;
    description = `Uttarayana (Northern Solar Course) active for a secular venture.`;
  } else if (context.ayana === 'Uttarayan') {
    category = 'AYANA';
    classification = 'INFORMATIONAL';
    description = `Uttarayana (Northern Solar Course) is recorded; no activity-specific rule applies to ${activityName}.`;
  } else if (/commerce|business|worldly|secular/.test(activityName.toLowerCase())) {
    category = 'AYANA';
    classification = 'WARNING';
    scoreImpact = -10;
    description = `Dakshinayana (Southern Solar Course) active: Requires careful vetting for secular worldly ventures.`;
  } else {
    category = 'AYANA';
    classification = 'INFORMATIONAL';
    description = `Dakshinayana (Southern Solar Course) is recorded; no activity-specific rule applies to ${activityName}.`;
  }

  return {
    factorId: 'FACT_PHASE17_CALENDAR_FACTS',
    factorName: 'Phase 17 Lunar/Solar Month, Ritu & Ayana Factors',
    category,
    classification,
    scoreImpact,
    description,
    evidence: context
  };
}
